import { css } from '@emotion/react';
import {
  Box,
  COLOR_PALETTE,
  Ellipsis,
  Flex,
  Icon,
  OptimizeImage,
  Text,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { faArrowUpRightFromSquare } from '@inflearn/pro-regular-svg-icons';
import { rem } from '@mantine/core';
import _ from 'lodash-es';

import { KoOnly } from '@inflearn/i18n-tools';
import {
  NEXT_PUBLIC_CDN_INTEGRATED_HOST,
  NEXT_PUBLIC_RALLIT_HOST,
} from '../../../../../../appConfig';
import useClientResumeHubSummaryQuery from '../../../../../queries/useClientResumeHubSummaryQuery';
import { eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../utils/GTM/useTrackMixpanelEvent';

export enum CareerStatus {
  IN_OFFICE = 'IN_OFFICE',
  IN_OFFER = 'IN_OFFER',
  IN_CHAT = 'IN_CHAT',
  LOOKING = 'LOOKING',
  OPEN_TO_FEEDBACK = 'OPEN_TO_FEEDBACK',
  EMPTY = 'EMPTY',
}

interface ProfileStatus {
  text: string;
  iconUrl: string;
  color: string;
}
const PROFILE_STATUS: Record<CareerStatus, ProfileStatus> = {
  [CareerStatus.IN_OFFICE]: {
    text: '지금 만족하고 있어요',
    iconUrl: '/apng/hugging-face.png',
    color: COLOR_PALETTE.rallit[8],
  },
  [CareerStatus.IN_CHAT]: {
    text: '가벼운 티타임 좋아요',
    iconUrl: '/apng/hot-beverage.png',
    color: COLOR_PALETTE.blue[8],
  },
  [CareerStatus.LOOKING]: {
    text: '이직/구직 중이에요',
    iconUrl: '/apng/heart-on-fire.png',
    color: COLOR_PALETTE.orange[8],
  },
  [CareerStatus.IN_OFFER]: {
    text: '좋은 제안에 열려있어요',
    iconUrl: '/apng/love-letter.png',
    color: COLOR_PALETTE.pink[8],
  },
  [CareerStatus.OPEN_TO_FEEDBACK]: {
    text: '프로필(이력서) 피드백 원해요',
    iconUrl: '/icon/memo.png',
    color: COLOR_PALETTE.violet[8],
  },
  [CareerStatus.EMPTY]: {
    text: '',
    iconUrl: '',
    color: '',
  },
} as const;

export const HubProfile = ({
  userId,
  userName,
}: {
  userId: number;
  userName: string;
}) => {
  return (
    <KoOnly>
      <_HubProfile userName={userName} userId={userId} />
    </KoOnly>
  );
};

export const _HubProfile = ({
  userId,
  userName,
}: {
  userId: number;
  userName: string;
}) => {
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { data } = useClientResumeHubSummaryQuery(userId, {
    refetchOnWindowFocus: true,
  });

  const status =
    (_.findKey(
      PROFILE_STATUS,
      (status: ProfileStatus) => status.text === data?.careerStatus,
    ) as CareerStatus) || 'EMPTY';

  const profileStatus = PROFILE_STATUS[status];

  const handleClick = () => {
    trackMixpanelEvent(eventNames.CLICK_USER_PROFILE_HUB, {
      user_id: userId,
    });
  };

  if (!data || status === 'EMPTY') {
    return <></>;
  }

  return (
    <Flex
      component={'a'}
      href={`${NEXT_PUBLIC_RALLIT_HOST}/hub/resumes/${data?.hubId}`}
      onClick={handleClick}
      target="_blank"
      rel="noopener"
      align="center"
      p={8}
      gap={8}
      w="fit-content"
      css={styleHubProfile}
    >
      <Box
        w={36}
        h={36}
        p={8}
        css={{
          flexShrink: 0,
          backgroundColor: 'white',
          borderRadius: '8px',
        }}
      >
        <OptimizeImage
          src={`${NEXT_PUBLIC_CDN_INTEGRATED_HOST}/image${profileStatus.iconUrl}`}
          width={20}
          height={20}
          alt={`${profileStatus.text} 아이콘`}
        />
      </Box>
      <Flex direction="column">
        <Flex
          w="100%"
          align="center"
          css={{
            '&:hover': {
              textDecoration: 'underline',

              [desktopFirstMediaQuery.tablet]: {
                textDecoration: 'inherit',
              },
            },
          }}
        >
          <Ellipsis lineClamp={1}>
            <Text
              size="sm"
              weight={500}
              maw={200}
              css={{
                /** 긴 영어단어의 경우 ellipsis가 제대로 적용되지 않음 */
                wordBreak: 'break-all',
                [desktopFirstMediaQuery.tablet]: {
                  maxWidth: rem(109),
                },
              }}
            >
              {userName}
            </Text>
          </Ellipsis>
          <Text size="sm" weight={500} css={{ flexShrink: 0 }}>
            의 랠릿 허브 프로필
          </Text>
          <Icon
            icon={faArrowUpRightFromSquare}
            size="xs"
            color="dark.6"
            aria-label="랠릿 허브 프로필로 이동"
            css={{
              display: 'none',
              marginLeft: rem(4),
              marginBottom: rem(1),
              [desktopFirstMediaQuery.tablet]: {
                display: 'block',
              },
            }}
          />
        </Flex>
        <Text size="xs" weight={700} color={profileStatus.color}>
          {profileStatus.text}
        </Text>
      </Flex>
    </Flex>
  );
};

const styleHubProfile = css({
  borderRadius: rem(8),
  backgroundColor: COLOR_PALETTE.gray[0],
  color: COLOR_PALETTE.dark[6],
  [desktopFirstMediaQuery.tablet]: {
    marginTop: 0,
  },
});
